import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { calcResizedUrlFromUuid } from '../lib/calc-resized-url';
import { capitalize } from '../lib/capitalize';
import { getAllRouteData, getAllRouteDatas } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { isNotFoundRoute } from '../lib/is-not-found-route';
import { doIndexMetaTag, getMetaImageTags, getMetaTitleTags } from '../lib/meta-helpers';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { StateService } from './state.service';
import { StoryblokService } from './storyblok.service';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private meta = inject(Meta);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private state = inject(StateService);
  private title = inject(Title);
  private browserService = inject(BrowserService);
  private config = inject(AppConfigService);
  private storyblokService = inject(StoryblokService);
  private document = inject<Document>(DOCUMENT);
  lastUrl: string;
  favIcons = ['.png', '.svg', '.ico', 'apple-touch-icon_GALLERY.png'];
  manifest: HTMLLinkElement;

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.handleMeta(event.urlAfterRedirects);
    });
    this.manifest = this.document.querySelector('#manifest');
  }

  private getFaviconPrefix(suffix: string) {
    const route = getFullPath(this.route.snapshot);
    let favicon = '/assets/favicon/favicon' + suffix;
    const isShared = route[1] === 'home' && this.state.sharedWithMeActiveTenant$.value;

    if (!isShared) {
      if (this.state.unseenActivity) favicon = '/assets/favicon/favicon-activity' + suffix;

      if (this.config.isTenantDomain) {
        const data = getAllRouteData(this.route.snapshot);
        const tenant = data.tenant || data.gallery?.tenant;
        const gallery = data.gallery;

        if (gallery && suffix.includes('GALLERY')) {
          if (gallery.teaser?.uuid) {
            const uuid = gallery.teaser.uuid;
            return `https://${this.config.env === 'production' ? '' : 'dev.'}picflow.media/images/resized/512x512/${uuid}.jpg`;
          }
        }

        if (tenant?.favicon_url) {
          favicon = tenant.favicon_url;
          const url = tenant.favicon_url;
          if (!url.includes('/original/')) return favicon; // new icons
          const uuid = url.split('/original/')[1].split('.')[0];
          favicon = calcResizedUrlFromUuid(this.config.assetsEndpoint, uuid, `512x512`);
        }
      } else {
        if (suffix.includes('GALLERY')) {
          return;
        }
      }
    }

    return favicon;
  }

  private getManifest() {
    if (!this.config.isTenantDomain) return;
    const data = getAllRouteData(this.route.snapshot);
    const tenant = data.tenant || data.gallery?.tenant;
    if (!tenant) return;
    if (!data.gallery) return `/manifest/${tenant.path}`;
    return addGalleryPassword(`/manifest/${tenant.id}/${data.gallery.id}`, data.gallery.password);
  }

  public setTitle(title: string) {
    const path = getFullPath(this.route.snapshot);
    if (path[0] === 'a') {
      if (path[1] === 'gallery') {
        const galleryId = path[2];
        const count = this.state.unseenActivityCountMap.get(galleryId);
        if (count) {
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      } else if (this.state.unseenActivity > 0) {
        const isShared = path[1] === 'home' && this.state.sharedWithMeActiveTenant$.value;

        if (!isShared) {
          const count = this.state.unseenActivity;
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      }
    }

    this.title.setTitle(title);
  }

  public async handleMeta(url: string = this.lastUrl) {
    if (!url && !this.lastUrl) return;
    this.lastUrl = url;
    const tags = this.getMetaTags();
    const title = this.getTitle();
    const imagesToPreload = this.storyblokService.getStoryImagesToPreload();
    imagesToPreload.forEach(image => {
      const link: HTMLLinkElement = this.document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('as', 'image');
      link.setAttribute('href', image);
      this.document.head.appendChild(link);
    });

    this.favIcons.forEach(suffix => {
      const el = this.document.getElementById(`FAVICON_${suffix}`) as HTMLLinkElement;
      if (el) {
        const href = this.getFaviconPrefix(suffix);
        if (!href) return;
        el.href = href;
      }
    });

    const manifestHref = this.getManifest() || '/manifest.webmanifest';
    if (this.manifest && !this.manifest.href.endsWith(manifestHref)) {
      this.manifest.href = manifestHref;
    }

    this.setTitle(title);

    const link: HTMLLinkElement =
      this.document.querySelector('link[rel="canonical"]') || this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    this.document.head.appendChild(link);
    link.setAttribute('href', this.getCanonical());
    tags.forEach(tag => this.meta.updateTag(tag));
  }

  private getCanonical() {
    const path = getFullPath(this.route.snapshot);
    const origin = this.browserService.getOrigin();
    return `${origin}/${path.join('/')}`;
  }

  private getTitleFromRoute(route: ActivatedRouteSnapshot): string {
    const path = getFullPath(route);
    if (isNotFoundRoute(path, { notFound: this.state.nextPageIsNotFound })) {
      return '404 · Page not found';
    }

    return capitalize(path.pop()) + ' · Picflow';
  }

  public getTitle() {
    const datas = getAllRouteDatas(this.route.snapshot);
    const title = datas.reduce((title, data) => {
      if (data.meta?.title) return data.meta.title as string;
      return title;
    }, undefined as string);

    if (title) return title;
    return this.getTitleFromRoute(this.route.snapshot);
  }

  private combineMetas(metas: { title?: string; tags?: MetaDefinition[] }[]): {
    title?: string;
    tags: MetaDefinition[];
  } {
    const combined: { title: string; tags: MetaDefinition[] } = {
      title: undefined,
      tags: [],
    };

    metas.forEach(meta => {
      if (meta.title) {
        combined.title = meta.title;
      }
      if (meta.tags) {
        meta.tags.forEach(tag => {
          // Avoid duplicates
          const index = combined.tags.findIndex(t => {
            if (t.name && tag.name) return t.name === tag.name;
            if (t.property && tag.property) return t.property === tag.property;
            return false;
          });
          if (index !== -1) {
            // remove the existing tag
            combined.tags.splice(index, 1);
          }

          combined.tags.push(tag);
        });
      }
    });

    return combined;
  }

  private getMetaTags() {
    const datas = getAllRouteDatas(this.route.snapshot);

    const globalTags: MetaDefinition[] = [
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:site_name',
        content: 'Picflow',
      },
      ...getMetaTitleTags(this.getTitleFromRoute(this.route.snapshot)),
      ...getMetaImageTags(`${this.config.correctPicflowUrl}/assets/sharing/default.jpg`),
      doIndexMetaTag,
      { property: 'og:url', content: this.getCanonical() },
      { property: 'theme-color', content: '#FFFFFF' },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
      },
    ];

    return this.combineMetas([
      {
        tags: globalTags,
      },
      ...datas.map(d => d.meta).filter(Boolean),
    ]).tags;
  }
}
